import { useCallback, useMemo, useRef } from 'react';
import { graphql, useFragment, useRelayEnvironment, fetchQuery } from 'react-relay';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { type usePartialItems_item$key } from './__generated__/usePartialItems_item.graphql.ts';
import { type usePartialItems_viewer$key } from './__generated__/usePartialItems_viewer.graphql.ts';

/* eslint-disable relay/unused-fields */
const partialItemsQuery = graphql`
    query usePartialItemsQuery(
        $itemIds: [String]!
        $isTrade: Boolean!
        $fetchTileVideo: Boolean!
        $showSeller: Boolean!
        $priceBookName: String = ""
        $pageDisplayEnum: PageDisplayEnum = searchAndBrowse
        $userCountryCode: String = ""
    ) {
        viewer {
            itemSearchInflateItems(itemIds: $itemIds) {
                edges {
                    node {
                        item {
                            serviceId
                            isPartialItem
                            ...FavoritesProvider_item
                            ...useSharedUrgencySignals_item
                            ...useItemShippingPrices_item
                            ...useSbSharedItemTracking_item
                            ...SharedItemTile_item
                                @arguments(
                                    isTrade: $isTrade
                                    fetchVideo: $fetchTileVideo
                                    priceBookName: $priceBookName
                                    showSeller: $showSeller
                                )
                        }
                    }
                }
            }
        }
    }
`;
/* eslint-enable relay/unused-fields */
const viewerFragment = graphql`
    fragment usePartialItems_viewer on Viewer {
        sbBatchLazyLoadFeatureFlag: featureFlag(feature: "sbBatchLazyLoad")
    }
`;
const itemFragment = graphql`
    fragment usePartialItems_item on Item @relay(plural: true) {
        serviceId
        isPartialItem
        medianL3SoldItemPriceScore
    }
`;
const BATCH_SIZE = 20;
export type PartialItemTrackingData = {
    medianL3SoldItemPriceScore: number;
};
type TrackingDataMap = {
    [itemId: string]: PartialItemTrackingData;
};
export type GetPartialItemTrackingData = (
    itemId: string | null | undefined
) => PartialItemTrackingData | null;

export const usePartialItems = (
    itemsRef: usePartialItems_item$key,
    viewerRef: usePartialItems_viewer$key
): {
    fetchPartialItems: (loadAll: boolean) => Promise<void>;
    getPartialItemTrackingData: GetPartialItemTrackingData;
    numOfInflatedItems: number;
    numOfPlaceholderItems: number;
} => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const isBatchingEnabled = viewer?.sbBatchLazyLoadFeatureFlag;
    const items = useFragment(itemFragment, itemsRef);
    const environment = useRelayEnvironment();
    const {
        numOfItemsToInflate,
        isTrade,
        fetchTileVideo = false,
        userCountryCode,
        priceBookName,
        showSeller,
    } = useSbSelector(state => state.relayVariables.variables);
    const trackingDataMap = useRef<TrackingDataMap>({});

    const { itemIds, numOfInflatedItems, numOfPlaceholderItems } = useMemo(() => {
        const ids: string[] = [];
        const inflatedItemIds = new Set<string>();
        let countInflatedItems = 0;
        let countPartialItems = 0;

        if (numOfItemsToInflate) {
            items.forEach(item => {
                if (item.isPartialItem && item.serviceId) {
                    ids.push(item.serviceId);
                    countPartialItems++;
                    if (item.medianL3SoldItemPriceScore) {
                        trackingDataMap.current[item.serviceId] = {
                            medianL3SoldItemPriceScore: item.medianL3SoldItemPriceScore || 0,
                        };
                    }
                } else if (item.serviceId) {
                    inflatedItemIds.add(item.serviceId);
                    countInflatedItems++;
                }
            });
        } else {
            countInflatedItems = items.length;
        }

        return {
            itemIds: ids,
            numOfInflatedItems: countInflatedItems,
            numOfPlaceholderItems: countPartialItems,
        };
    }, [items, numOfItemsToInflate]);

    const fetchPartialItems = useCallback(
        async (loadAll: boolean): Promise<void> => {
            if (!itemIds.length) {
                return;
            }
            let batchOfIds = itemIds;
            // checking for 1.5 batch size before slicing, don't want to leave last batch with just few items
            if (isBatchingEnabled && !loadAll && itemIds.length >= BATCH_SIZE + BATCH_SIZE / 2) {
                batchOfIds = itemIds.slice(0, BATCH_SIZE);
            }

            // need to await here to correctly catch the error in the SbSharedPartialItemsPlaceholder component
            await fetchQuery(environment, partialItemsQuery, {
                itemIds: batchOfIds,
                pageDisplayEnum: 'searchAndBrowse',
                showSeller,
                isTrade,
                fetchTileVideo,
                userCountryCode,
                priceBookName,
            }).toPromise();
        },
        [
            environment,
            showSeller,
            isTrade,
            fetchTileVideo,
            userCountryCode,
            priceBookName,
            itemIds,
            isBatchingEnabled,
        ]
    );

    const getPartialItemTrackingData: GetPartialItemTrackingData = useCallback(itemId => {
        return itemId && hasKey(trackingDataMap.current, itemId)
            ? trackingDataMap.current[itemId]
            : null;
    }, []);

    return {
        fetchPartialItems,
        getPartialItemTrackingData,
        numOfInflatedItems,
        numOfPlaceholderItems,
    };
};
